import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios"; // Import Axios for API calls
import { useInView } from "@/hooks/useInView";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/useToast";
import { plans } from "./PricingSection"; // Assuming this is defined correctly

// --- Zod Schema for Validation ---
// Define the validation schema for the form data
const registrationSchema = z.object({
  // Personal Information
  name: z.string().min(1, "Full Name is required"),
  email: z
    .string()
    .email("Invalid email address")
    .optional()
    .or(z.literal(""))
    .transform((e) => (e === "" ? undefined : e)), // Make email optional
  age: z
    .string()
    .optional()
    .refine(
      (val) => !val || (Number(val) >= 16 && Number(val) <= 100),
      "Age must be between 16 and 100",
    ),
  height: z
    .string()
    .optional()
    .refine(
      (val) => !val || (Number(val) >= 100 && Number(val) <= 250),
      "Height must be between 100 and 250 cm",
    ),
  weight: z
    .string()
    .optional()
    .refine(
      (val) => !val || (Number(val) >= 30 && Number(val) <= 200),
      "Weight must be between 30 and 200 kg",
    ),
  gender: z.string().min(1, "Gender is required"),
  phoneNumber: z
    .string()
    .min(1, "Phone Number is required")
    .regex(
      /^\+?\d{9,15}$/,
      "Invalid phone number format (e.g., +2519... or 09...)",
    ), // Basic Ethiopian/International check
  address: z.string().min(1, "Address is required"),
  bloodType: z.string().optional(),
  exerciseHoursPerWeek: z
    .string()
    .min(1, "Exercise hours are required")
    .refine(
      (val) => Number(val) >= 0 && Number(val) <= 50,
      "Hours must be between 0 and 50",
    ),

  // Dietary Preferences
  dietaryPreferences: z.array(z.string()).optional(),
  allergies: z.string().optional(),

  // Package & Goals
  package: z.string().min(1, "Package selection is required"),
  lifestyleGoals: z.string().optional(),
});

// Type derivation from Zod schema
type FormData = z.infer<typeof registrationSchema>;

const RegistrationSection = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDietary, setSelectedDietary] = useState<string[]>([]);
  const { ref: headerRef, isInView: headerInView } = useInView({
    threshold: 0.2,
  });
  const { ref: formRef, isInView: formInView } = useInView({ threshold: 0.1 });

  // Initialize useForm with Zod resolver and default values
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      name: "",
      email: "",
      age: "",
      height: "",
      weight: "",
      gender: "",
      phoneNumber: "",
      address: "",
      bloodType: "",
      exerciseHoursPerWeek: "",
      dietaryPreferences: [],
      allergies: "",
      package: "",
      lifestyleGoals: "",
    },
  });

  // Watch package and gender fields for manual component updates (e.g., Select)
  const watchedPackage = watch("package");
  const watchedGender = watch("gender");
  const watchedBloodType = watch("bloodType");

  const dietaryOptions = [
    "Vegetarian",
    "Vegan",
    "Keto",
    "Low Carb",
    "High Protein",
    "Mediterranean",
    "Traditional Ethiopian",
    "Gluten-Free",
  ];

  const handleDietaryChange = (option: string) => {
    const newSelection = selectedDietary.includes(option)
      ? selectedDietary.filter((item) => item !== option)
      : [...selectedDietary, option];

    setSelectedDietary(newSelection);
    // Manually set the value for React Hook Form
    setValue("dietaryPreferences", newSelection, { shouldValidate: true });
  };

  // --- API Submission Handler ---
  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsLoading(true);

    // Mock API endpoint for demonstration
    const API_ENDPOINT =
      "https://n8n.panova.dev/webhook/revivemeais/auth/register";

    try {
      // In a real application, you would post the 'data' object
      const response = await axios.post(API_ENDPOINT, data);

      if (response.status === 200) {
        toast({
          title: "Registration Successful! 🎉",
          description:
            "Welcome to Revive Meal Ethiopia! We'll contact you within 24 hours to finalize your meal plan.",
        });

        // Reset the form after successful submission
        reset();
        setSelectedDietary([]);
      }
    } catch (error) {
      console.error("Registration error:", error);
      // Determine the error message
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message ||
          "Could not connect to server. Please try again."
        : "An unexpected error occurred.";

      toast({
        title: "Registration Failed 😥",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="registration" className="section-padding bg-gradient-subtle">
      <div className="container-narrow">
        <div
          ref={headerRef}
          className={`text-center space-y-6 mb-12 transition-all duration-1000 ${
            headerInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold font-heading text-foreground">
            Start Your <span className="text-primary">Health Journey</span>
          </h2>
          <p className="text-xl text-muted-foreground font-body leading-relaxed max-w-3xl mx-auto">
            Tell us about yourself so we can create the perfect meal plan
            tailored to your needs and goals.
          </p>
        </div>

        <div
          ref={formRef}
          className={`bg-white rounded-3xl shadow-medium p-8 sm:p-12 transition-all duration-1000 ${
            formInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold font-heading text-foreground border-b border-border pb-2">
                Personal Information
              </h3>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    className="input-focus border-border rounded-xl"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Combined Email and Phone Number for new layout */}
                <div className="space-y-2">
                  <Label
                    htmlFor="phoneNumber"
                    className="font-body font-medium"
                  >
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+251 911 123 456"
                    className="input-focus border-border rounded-xl"
                    {...register("phoneNumber")}
                  />
                  {errors.phoneNumber && (
                    <p className="text-sm text-red-500">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    className="input-focus border-border rounded-xl"
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender" className="font-body font-medium">
                    Gender *
                  </Label>
                  {/* Using controlled select component */}
                  <Select
                    value={watchedGender}
                    onValueChange={(value) =>
                      setValue("gender", value, { shouldValidate: true })
                    }
                  >
                    <SelectTrigger className="w-full input-focus border-border rounded-xl">
                      <SelectValue placeholder="Select your gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.gender && (
                    <p className="text-sm text-red-500">
                      {errors.gender.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="age" className="font-body font-medium">
                    Age
                  </Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="25"
                    className="input-focus border-border rounded-xl"
                    {...register("age")}
                  />
                  {errors.age && (
                    <p className="text-sm text-red-500">{errors.age.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="height" className="font-body font-medium">
                    Height (cm)
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    placeholder="170"
                    className="input-focus border-border rounded-xl"
                    {...register("height")}
                  />
                  {errors.height && (
                    <p className="text-sm text-red-500">
                      {errors.height.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight" className="font-body font-medium">
                    Weight (kg)
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="70"
                    className="input-focus border-border rounded-xl"
                    {...register("weight")}
                  />
                  {errors.weight && (
                    <p className="text-sm text-red-500">
                      {errors.weight.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="font-body font-medium">
                  Address *
                </Label>
                <Textarea
                  id="address"
                  placeholder="Enter your full address (street, city, region)"
                  className="input-focus border-border rounded-xl min-h-20"
                  {...register("address")}
                />
                {errors.address && (
                  <p className="text-sm text-red-500">
                    {errors.address.message}
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="bloodType" className="font-body font-medium">
                    Blood Type (Optional)
                  </Label>
                  {/* Using controlled select component */}
                  <Select
                    value={watchedBloodType}
                    onValueChange={(value) =>
                      setValue("bloodType", value, { shouldValidate: true })
                    }
                  >
                    <SelectTrigger className="w-full input-focus border-border rounded-xl">
                      <SelectValue placeholder="Select your blood type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A+">A+</SelectItem>
                      <SelectItem value="A-">A-</SelectItem>
                      <SelectItem value="B+">B+</SelectItem>
                      <SelectItem value="B-">B-</SelectItem>
                      <SelectItem value="AB+">AB+</SelectItem>
                      <SelectItem value="AB-">AB-</SelectItem>
                      <SelectItem value="O+">O+</SelectItem>
                      <SelectItem value="O-">O-</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="exerciseHours"
                    className="font-body font-medium"
                  >
                    Exercise Hours per Week *
                  </Label>
                  <Input
                    id="exerciseHours"
                    type="number"
                    placeholder="5"
                    className="input-focus border-border rounded-xl"
                    step="0.5"
                    {...register("exerciseHoursPerWeek")}
                  />
                  {errors.exerciseHoursPerWeek && (
                    <p className="text-sm text-red-500">
                      {errors.exerciseHoursPerWeek.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Dietary Preferences */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold font-heading text-foreground border-b border-border pb-2">
                Dietary Preferences
              </h3>

              <div className="space-y-4">
                <Label className="font-body font-medium text-base">
                  Select all that apply:
                </Label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {dietaryOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleDietaryChange(option)}
                      className={`p-3 rounded-xl border font-body text-sm transition-all ${
                        selectedDietary.includes(option)
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-muted-foreground border-border hover:border-primary hover:text-primary"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allergies" className="font-body font-medium">
                  Food Allergies & Restrictions
                </Label>
                <Textarea
                  id="allergies"
                  placeholder="Please list any food allergies, foods you don't like, intolerances, or restrictions we should know about..."
                  className="input-focus border-border rounded-xl min-h-20"
                  {...register("allergies")}
                />
              </div>
            </div>

            {/* Package Selection */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold font-heading text-foreground border-b border-border pb-2">
                Package Selection
              </h3>

              <div className="space-y-2">
                <Label htmlFor="package" className="font-body font-medium">
                  Choose Your Plan *
                </Label>
                {/* Using controlled select component */}
                <Select
                  value={watchedPackage}
                  onValueChange={(value) =>
                    setValue("package", value, { shouldValidate: true })
                  }
                >
                  <SelectTrigger className="w-full input-focus border-border rounded-xl">
                    <SelectValue placeholder="Select a meal plan package" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.name} value={plan.name}>
                        {plan.name} - ETB {plan.price}/{plan.period} (
                        {plan.description})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.package && (
                  <p className="text-sm text-red-500">
                    {errors.package.message}
                  </p>
                )}
              </div>
            </div>

            {/* Lifestyle Goals */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold font-heading text-foreground border-b border-border pb-2">
                Health & Lifestyle Goals
              </h3>

              <div className="space-y-2">
                <Label htmlFor="goals" className="font-body font-medium">
                  What are your main health goals?
                </Label>
                <Textarea
                  id="goals"
                  placeholder="E.g., lose weight, build muscle, improve energy, manage diabetes, eat healthier, save time cooking..."
                  className="input-focus border-border rounded-xl min-h-24"
                  {...register("lifestyleGoals")}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                size="lg"
                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold btn-hover-lift text-lg py-4"
                disabled={isLoading} // Disable button while loading
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  <>
                    <span className="sm:hidden">Complete Registration</span>
                    <span className="hidden sm:inline">
                      Complete Registration & Start Your Journey
                    </span>
                  </>
                )}
              </Button>

              <p className="text-center mt-4 text-sm text-muted-foreground font-body">
                By registering, you agree to our terms of service and privacy
                policy. We'll contact you within 24 hours to confirm your meal
                plan details.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RegistrationSection;
